import React from "react";
import styled from "styled-components";
import type { ComponentProps } from "widgets/BaseComponent";
import { AlignWidgetTypes } from "WidgetProvider/constants";
import { Classes } from "@blueprintjs/core";
import { Colors } from "constants/Colors";
import { LabelPosition } from "components/constants";
import { FontStyleTypes } from "constants/WidgetConstants";
import { Checkbox } from "components/wds";
import { useIntl } from "react-intl";

interface StyledCheckboxContainerProps {
  isValid: boolean;
  noContainerPadding?: boolean;
  labelPosition?: LabelPosition;
  minHeight?: number;
}

const DEFAULT_BORDER_RADIUS = "0";
const DEFAULT_BACKGROUND_COLOR = Colors.GREEN_SOLID;

const CheckboxContainer = styled.div<StyledCheckboxContainerProps>`
  && {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: start;
    width: 100%;

    ${({ minHeight }) => `
    ${minHeight ? `min-height: ${minHeight}px;` : ""}`};

    .${Classes.CHECKBOX} {
      width: 100%;
    }
  }
`;

export const CheckboxLabel = styled.div<{
  disabled?: boolean;
  alignment: AlignWidgetTypes;
  labelTextColor?: string;
  labelTextSize?: string;
  labelStyle?: string;
  isDynamicHeightEnabled?: boolean;
  isLabelInline?: boolean;
}>`
  width: 100%;
  display: inline-block;
  vertical-align: top;
  text-align: ${({ alignment }) => alignment.toLowerCase()};
  ${({ disabled, labelStyle, labelTextColor, labelTextSize }) => `
  color: ${
    disabled ? "var(--wds-color-text-disabled)" : labelTextColor || "inherit"
  };
  font-size: ${labelTextSize ?? "inherit"};
  font-weight: ${labelStyle?.includes(FontStyleTypes.BOLD) ? "bold" : "normal"};
  font-style: ${
    labelStyle?.includes(FontStyleTypes.ITALIC) ? "italic" : "normal"
  };
  `}

  ${({ isDynamicHeightEnabled }) =>
    isDynamicHeightEnabled ? "&& { word-break: break-all; }" : ""};

  ${({ isLabelInline }) =>
    isLabelInline &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-wrap: normal;
  `}
`;

export const StyledCheckbox = styled(Checkbox)`
  &.bp3-control.bp3-align-right {
    padding-right: 0px;
  }
`;

const RequiredAsterisk = styled.span`
  color: ${Colors.DANGER_SOLID};
  margin-right: 4px;
`;

const CheckboxComponent: React.FC<CheckboxComponentProps> = (props) => {
  const intl = useIntl();
  
  /**
   * When the label position is left align checkbox to the right
   * When the label position is right align checkbox to the left
   */
  const checkboxAlignClass =
    props.labelPosition === LabelPosition.Right ? "left" : "right";

  // If the prop isValid has a value true/false (it was explicitly passed to this component),
  // it take priority over the internal logic to determine if the field is valid or not.
  const isValid = (() => {
    if (props.isValid !== undefined) {
      return props.isValid;
    }

    return !(props.isRequired && !props.isChecked);
  })();
  
  const onCheckChange = () => {
    props.onCheckChange(!props.isChecked);
  };

  return (
    <CheckboxContainer
      isValid={isValid}
      minHeight={props.minHeight}
      noContainerPadding={props.noContainerPadding}
    >
      {props.isReadOnly ? (
        <div className={props.isLoading ? Classes.SKELETON : Classes.RUNNING_TEXT} style={{ display: "flex", width: "100%", alignItems: "center", gap: 8 }}>
          <CheckboxLabel
            alignment={props.alignWidget || AlignWidgetTypes.LEFT}
            className="t--checkbox-widget-label"
            disabled={false}
            isDynamicHeightEnabled={props.isDynamicHeightEnabled}
            isLabelInline={props.isLabelInline}
            labelStyle={props.labelStyle}
            labelTextColor={props.labelTextColor}
            labelTextSize={props.labelTextSize}
          >
            {props.isRequired ? <RequiredAsterisk>*</RequiredAsterisk> : null}
            {props.label}
          </CheckboxLabel>
          <div style={{ flexShrink: 0 }}>
            {(props as any).displayTextOverride ?? intl.formatMessage({ 
              id: props.isChecked ? "widget.checkbox.status.checked" : "widget.checkbox.status.unchecked" 
            })}
          </div>
        </div>
      ) : (
        <StyledCheckbox
          accentColor={props.accentColor || DEFAULT_BACKGROUND_COLOR}
          alignIndicator={checkboxAlignClass}
          borderRadius={props.borderRadius || DEFAULT_BORDER_RADIUS}
          checked={props.isChecked}
          className={
            props.isLoading ? Classes.SKELETON : Classes.RUNNING_TEXT
          }
          disabled={props.isDisabled}
          hasError={!isValid}
          inputRef={props.inputRef}
          labelElement={
            <CheckboxLabel
              alignment={props.alignWidget || AlignWidgetTypes.LEFT}
              className="t--checkbox-widget-label"
              disabled={props.isDisabled}
              isDynamicHeightEnabled={props.isDynamicHeightEnabled}
              isLabelInline={props.isLabelInline}
              labelStyle={props.labelStyle}
              labelTextColor={props.labelTextColor}
              labelTextSize={props.labelTextSize}
            >
              {props.isRequired ? <RequiredAsterisk>*</RequiredAsterisk> : null}
              {props.label}
            </CheckboxLabel>
          }
          onChange={onCheckChange}
        />
      )}
    </CheckboxContainer>
  );
};

export interface CheckboxComponentProps extends ComponentProps {
  alignWidget?: AlignWidgetTypes;
  noContainerPadding?: boolean;
  isChecked: boolean;
  isLoading: boolean;
  isRequired?: boolean;
  isValid?: boolean;
  label: string;
  onCheckChange: (isChecked: boolean) => void;
  inputRef?: (el: HTMLInputElement | null) => any;
  accentColor: string;
  borderRadius: string;
  isDynamicHeightEnabled?: boolean;
  labelPosition: LabelPosition;
  labelTextColor?: string;
  labelTextSize?: string;
  labelStyle?: string;
  isLabelInline?: boolean;
  minHeight?: number;
  isReadOnly?: boolean;
}

export default CheckboxComponent;
